/**
 * DroitsEquipe — RP36-RP37 (D4, fiche 02 COL-A9) : écran cabinet « Droits de l'équipe ».
 *
 * Six droits lisibles, réglés une fois par personne, appliqués sur tous les projets.
 * Le réglage fin d'un projet (Équipe du projet › Modifier › Avancé) l'emporte.
 * Onglet « Historique » : tous les changements de droits (cabinet et projets) —
 * l'historique a quitté la fiche membre du projet.
 *
 * Endpoints : GET/PUT /api/cabinet/droits-equipe[/{user_id}],
 *             GET /api/cabinet/droits-equipe/historique
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, ClockCounterClockwise } from "@phosphor-icons/react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  } catch (e) {
    return iso;
  }
}

export default function DroitsEquipe() {
  const [onglet, setOnglet] = useState("droits");
  const [data, setData] = useState({ droits: [], personnes: [] });
  const [historique, setHistorique] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/cabinet/droits-equipe`, { withCredentials: true });
      setData(r.data || { droits: [], personnes: [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger les droits de l'équipe");
    } finally {
      setLoading(false);
    }
  }, []);

  const chargerHistorique = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/cabinet/droits-equipe/historique`, { withCredentials: true });
      setHistorique(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setHistorique([]);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { if (onglet === "historique") chargerHistorique(); }, [onglet, chargerHistorique]);

  const regler = async (personne, droit, valeur) => {
    // Revenir à la valeur du rôle = « par défaut » (le réglage suit alors le rôle).
    const envoi = valeur === droit.par_defaut ? null : valeur;
    setSaving(`${personne.user_id}:${droit.cle}`);
    try {
      const r = await axios.put(
        `${API_URL}/api/cabinet/droits-equipe/${personne.user_id}`,
        { droits: { [droit.cle]: envoi } },
        { withCredentials: true }
      );
      setData((prev) => ({
        ...prev,
        personnes: prev.personnes.map((p) => (p.user_id === personne.user_id ? r.data : p)),
      }));
      toast.success("Droit mis à jour sur tous les chantiers");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setSaving(null);
    }
  };

  const libelleDroit = (cle) => (data.droits.find((d) => d.cle === cle) || {}).libelle || cle;

  return (
    <section className="mt-10" data-testid="droits-equipe">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h2 className="text-2xl text-[#2C2C2C]" style={{ fontFamily: "DM Serif Display, serif" }}>
            Droits de l'équipe
          </h2>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Réglés une fois par personne, appliqués sur tous les projets. Un réglage fin sur un projet
            (Équipe du projet › Modifier › Avancé) l'emporte.
          </p>
        </div>
        <div className="flex border border-[#E0E0DB]" role="tablist">
          <button
            type="button" role="tab" aria-selected={onglet === "droits"}
            onClick={() => setOnglet("droits")}
            className={`px-3 py-1.5 text-sm inline-flex items-center gap-1 ${onglet === "droits" ? "bg-[#2C2C2C] text-white" : "text-[#2C2C2C]"}`}
            data-testid="droits-equipe-onglet-droits"
          >
            <ShieldCheck size={14} /> Droits
          </button>
          <button
            type="button" role="tab" aria-selected={onglet === "historique"}
            onClick={() => setOnglet("historique")}
            className={`px-3 py-1.5 text-sm inline-flex items-center gap-1 ${onglet === "historique" ? "bg-[#2C2C2C] text-white" : "text-[#2C2C2C]"}`}
            data-testid="droits-equipe-onglet-historique"
          >
            <ClockCounterClockwise size={14} /> Historique
          </button>
        </div>
      </div>

      {onglet === "droits" ? (
        loading ? (
          <p className="text-sm text-[#9A9A9A]">Chargement…</p>
        ) : (
          <div className="overflow-x-auto border border-[#E0E0DB] bg-white">
            <table className="w-full text-sm" data-testid="droits-equipe-table">
              <thead>
                <tr className="bg-[#F5F5F3] text-left">
                  <th className="p-2 font-medium text-[#2C2C2C] min-w-[160px]">Personne</th>
                  {data.droits.map((d) => (
                    <th key={d.cle} className="p-2 font-medium text-[#2C2C2C] text-center min-w-[110px]" title={d.aide}>
                      {d.libelle}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.personnes.map((p) => (
                  <tr key={p.user_id} className="border-t border-[#E0E0DB]" data-testid={`droits-equipe-ligne-${p.user_id}`}>
                    <td className="p-2 align-top">
                      <div className="font-medium text-[#2C2C2C]">{p.name}</div>
                      <div className="text-xs text-[#6B6B6B]">{p.libelle_role}</div>
                      {p.note && <div className="text-[11px] text-[#9A9A9A] mt-0.5">{p.note}</div>}
                    </td>
                    {p.droits.map((d) => (
                      <td key={d.cle} className="p-2 text-center align-top">
                        <label className="inline-flex flex-col items-center gap-0.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!d.valeur}
                            disabled={!p.modifiable || saving === `${p.user_id}:${d.cle}`}
                            onChange={(e) => regler(p, d, e.target.checked)}
                            aria-label={`${libelleDroit(d.cle)} — ${p.name}`}
                            data-testid={`droit-${p.user_id}-${d.cle}`}
                          />
                          <span className="text-[10px] text-[#9A9A9A]">
                            {d.valeur ? "Autorisé" : "Non autorisé"}{d.regle === null && p.modifiable ? " (par défaut)" : ""}
                          </span>
                        </label>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <div className="border border-[#E0E0DB] bg-white" data-testid="droits-equipe-historique">
          {historique.length === 0 ? (
            <p className="text-sm text-[#9A9A9A] p-4 text-center">Aucun changement de droits pour l'instant.</p>
          ) : (
            <ul className="divide-y divide-[#E0E0DB]">
              {historique.map((h) => (
                <li key={h.id} className="p-3 text-sm">
                  <div className="text-xs text-[#6B6B6B]">
                    {formatDate(h.timestamp)} · {h.auteur} → <span className="font-medium text-[#2C2C2C]">{h.personne}</span>
                  </div>
                  <div className="text-[#2C2C2C] mt-0.5">{h.resume}</div>
                  {h.note && <div className="text-xs italic text-[#6B6B6B] mt-0.5">« {h.note} »</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
